import {
  calibrationStats,
  deriveBikeFrame,
  dot,
  leanFromOrientation,
  leanKinematic,
  lowpass,
  orientationMatrix,
  STANDARD_GRAVITY,
  scale,
  subtract,
  type BikeFrame,
  type Vec3,
} from '@motogo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { GpsPosition } from '@/types/geo';

export type MotionStatus =
  | 'idle' // nothing requested yet — show the calibrate intro
  | 'requesting' // permission prompt up / waiting for the first sensor event
  | 'denied' // iOS motion permission denied — re-prompting is not possible, needs browser settings
  | 'unsupported' // no DeviceMotionEvent, or granted but no events ever arrive (desktop)
  | 'calibrating' // sampling the stationary reference
  | 'calibration-failed' // too much movement during the window
  | 'active';

export interface TrailSample {
  at: number; // Date.now()
  longG: number;
  latG: number;
}

export interface MotionTelemetry {
  /**
   * Longitudinal acceleration in g — positive accelerating, negative braking.
   * Complementary-blended: IMU transients + d(GPS speed)/dt for the sustained part,
   * so a held pull doesn't wash out (see BLEND_TAU_MS).
   */
  longG: number;
  /**
   * Lateral acceleration in g — positive to the rider's right. Blended with
   * v·yawRate for the sustained part: the bike-frame IMU reads ~0 in a coordinated
   * corner (leaning aligns the net force with the bike), so IMU alone can't see
   * sustained cornering load at all.
   */
  latG: number;
  /** Lean from fused orientation (under-reads in long steady corners). Positive = right. */
  leanImuDeg: number | null;
  /** Lean from v·yawRate/g — needs GPS speed, null when stationary/unknown. */
  leanKinDeg: number | null;
  /** Session peaks since calibration, for eyeballing against the bike's own IMU. */
  peakAccelG: number;
  peakBrakeG: number;
  peakLatG: number;
  /** Recent samples for the G-G trail, oldest first. */
  trail: TrailSample[];
}

const EMPTY_TELEMETRY: MotionTelemetry = {
  longG: 0,
  latG: 0,
  leanImuDeg: null,
  leanKinDeg: null,
  peakAccelG: 0,
  peakBrakeG: 0,
  peakLatG: 0,
  trail: [],
};

const CALIBRATION_MS = 1500;
// Worst single-sample deviation from the window mean before we call it movement.
// Idling engines vibrate; this is loose enough for a running twin on the stand.
const MAX_CALIBRATION_DEVIATION = 0.9; // m/s²
const FIRST_EVENT_TIMEOUT_MS = 2000;
const PUBLISH_MS = 66; // ~15 Hz to React state; sensors stay at native rate in refs
const ACCEL_TAU_MS = 180;
const YAW_TAU_MS = 250;
const TRAIL_MS = 4000;
// Below this speed the kinematic lean is meaningless (parking-lot paddling).
const KIN_LEAN_MIN_MPS = 2;

// Complementary blend for long/lat G. The platform's gravity-removed acceleration
// washes out under sustained load — its gravity estimator slowly absorbs any
// acceleration held for a few seconds (big spike on launch, fades while still pulling,
// phantom "braking" rebound when easing off). So the IMU is only trusted for
// transients (high-passed), and the sustained part comes from references that cannot
// wash out: d(GPS speed)/dt for longitudinal, v·yawRate for lateral. τ is the
// crossover — shorter trusts GPS more (laggier transients), longer lets more IMU
// washout through.
const BLEND_TAU_MS = 1500;
// GPS-accel derivative windows outside these bounds are rejected (duplicate fix /
// dropout), and a sample older than the max age stops being a usable reference.
const GPS_ACCEL_MIN_DT_MS = 200;
const GPS_ACCEL_MAX_DT_MS = 3000;
const GPS_ACCEL_MAX_AGE_MS = 3000;

/** iOS 13+ static — not in lib.dom for all targets, so typed locally. */
interface PermissionGated {
  requestPermission?: () => Promise<string>;
}

const CALIBRATION_KEY = 'motogo.telemetry.calibration';

/**
 * A saved calibration is only as good as the assumption that the phone sits
 * in its mount exactly as it did when saved — the UI warns about that before
 * offering it.
 */
interface StoredCalibration {
  up: Vec3;
  forward: Vec3;
  right: Vec3;
  gravity: Vec3;
  savedAt: number;
}

function isVec3(v: unknown): v is Vec3 {
  return Array.isArray(v) && v.length === 3 && v.every((n) => Number.isFinite(n));
}

function loadStoredCalibration(): StoredCalibration | null {
  try {
    const raw = localStorage.getItem(CALIBRATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredCalibration>;
    if (!isVec3(parsed.up) || !isVec3(parsed.forward) || !isVec3(parsed.right) || !isVec3(parsed.gravity)) {
      return null;
    }
    return parsed as StoredCalibration;
  } catch {
    return null;
  }
}

function storeCalibration(calibration: StoredCalibration): void {
  try {
    localStorage.setItem(CALIBRATION_KEY, JSON.stringify(calibration));
  } catch {
    // localStorage unavailable — restore just won't be offered next session
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitFor(condition: () => boolean, timeoutMs: number): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (condition()) return true;
    await delay(50);
  }
  return condition();
}

export interface UseMotionResult {
  status: MotionStatus;
  telemetry: MotionTelemetry;
  /**
   * Requests motion permission (must be called from a user gesture — iOS shows
   * its prompt only then) and runs the stationary calibration window. Also
   * serves as re-calibrate from any state.
   */
  calibrate: () => Promise<void>;
  /** True when a calibration from an earlier session is available to restore. */
  hasSavedCalibration: boolean;
  /**
   * Skips the sampling window and reuses the saved bike frame. Still needs the
   * permission/user-gesture dance, so it must also be called from a tap. Only
   * valid while the phone sits in its mount exactly as it did when saved.
   */
  restoreCalibration: () => Promise<void>;
}

/**
 * Owns the devicemotion/deviceorientation lifecycle: permission, the
 * calibration window that maps phone axes onto bike axes, filtering, and the
 * derived telemetry (long/lat g, two independent lean estimates). Sensor
 * events are consumed at native rate into refs; React state is published at
 * ~15 Hz. `position` supplies GPS speed for the kinematic lean and the
 * complementary blends — the whole fix object, because a fresh object per fix
 * is what lets the speed derivative advance through identical readings
 * (stationary, steady cruise).
 */
export function useMotion(position: GpsPosition | null): UseMotionResult {
  const [status, setStatus] = useState<MotionStatus>('idle');
  const [telemetry, setTelemetry] = useState<MotionTelemetry>(EMPTY_TELEMETRY);
  const [hasSavedCalibration, setHasSavedCalibration] = useState(
    () => loadStoredCalibration() !== null,
  );

  const speedRef = useRef(position?.speed ?? null);
  speedRef.current = position?.speed ?? null;

  // GPS-speed derivative — the washout-proof longitudinal reference (in g).
  const gpsAccelRef = useRef<{ g: number; at: number } | null>(null);
  const prevSpeedFixRef = useRef<{ v: number; at: number } | null>(null);

  useEffect(() => {
    const speed = position?.speed;
    if (speed == null || Number.isNaN(speed)) {
      prevSpeedFixRef.current = null;
      return;
    }
    const now = performance.now();
    const prev = prevSpeedFixRef.current;
    prevSpeedFixRef.current = { v: speed, at: now };
    if (!prev) return;
    const dtMs = now - prev.at;
    if (dtMs < GPS_ACCEL_MIN_DT_MS || dtMs > GPS_ACCEL_MAX_DT_MS) return;
    gpsAccelRef.current = {
      g: ((speed - prev.v) / (dtMs / 1000)) / STANDARD_GRAVITY,
      at: now,
    };
  }, [position]);

  // iOS WebKit reports accelerometer values with inverted signs vs the W3C
  // spec; the requestPermission static doubles as the platform detector.
  const invertRef = useRef(false);
  const frameRef = useRef<BikeFrame | null>(null);
  const gravityRef = useRef<Vec3>([0, 0, 0]); // calibrated mean, for the no-gyro fallback
  const listenersRef = useRef(false);
  const sawEventRef = useRef(false);
  const collectingRef = useRef(false);
  const calibrationSamplesRef = useRef<Vec3[]>([]);
  const lastMotionAtRef = useRef(0);
  const filteredAccelRef = useRef<[number, number, number]>([0, 0, 0]);
  const filteredYawRef = useRef(0);
  const sawGyroRef = useRef(false); // v·yawRate is only a usable lateral reference with a gyro
  // Complementary-blend low-pass states (all in g): reference and IMU per axis.
  const lpRefLongRef = useRef(0);
  const lpImuLongRef = useRef(0);
  const lpRefLatRef = useRef(0);
  const lpImuLatRef = useRef(0);
  const valuesRef = useRef<MotionTelemetry>({ ...EMPTY_TELEMETRY });
  const trailRef = useRef<TrailSample[]>([]);

  const onMotion = useCallback((e: DeviceMotionEvent) => {
    sawEventRef.current = true;
    const sign = invertRef.current ? -1 : 1;

    const withGravity = e.accelerationIncludingGravity;
    if (collectingRef.current && withGravity?.x != null && withGravity.y != null && withGravity.z != null) {
      calibrationSamplesRef.current.push([
        sign * withGravity.x,
        sign * withGravity.y,
        sign * withGravity.z,
      ]);
    }

    const frame = frameRef.current;
    if (!frame) return;

    const now = performance.now();
    const dt = lastMotionAtRef.current ? Math.min(100, now - lastMotionAtRef.current) : 16;
    lastMotionAtRef.current = now;

    // Yaw rate about the bike's up axis → kinematic lean + the lateral blend
    // reference below. rotationRate is deg/s about the phone axes (beta:x,
    // gamma:y, alpha:z), right-hand rule — so a right turn is a *negative* rate
    // about up; flip to right-positive.
    const r = e.rotationRate;
    if (r?.alpha != null && r.beta != null && r.gamma != null) {
      sawGyroRef.current = true;
      const omega: Vec3 = [
        (r.beta * Math.PI) / 180,
        (r.gamma * Math.PI) / 180,
        (r.alpha * Math.PI) / 180,
      ];
      filteredYawRef.current = lowpass(filteredYawRef.current, -dot(omega, frame.up), dt, YAW_TAU_MS);
      const speed = speedRef.current;
      valuesRef.current.leanKinDeg =
        speed != null && speed > KIN_LEAN_MIN_MPS
          ? leanKinematic(speed, filteredYawRef.current)
          : null;
    }

    // Linear acceleration: prefer the platform's gravity-removed value (sensor
    // fusion, correct at any lean). Devices without a gyro only provide
    // accelerationIncludingGravity — subtracting the calibrated gravity vector
    // is then only right with the bike upright, but it's the best available.
    let linear: Vec3 | null = null;
    const a = e.acceleration;
    if (a?.x != null && a.y != null && a.z != null) {
      linear = [sign * a.x, sign * a.y, sign * a.z];
    } else if (withGravity?.x != null && withGravity.y != null && withGravity.z != null) {
      linear = subtract(
        [sign * withGravity.x, sign * withGravity.y, sign * withGravity.z],
        gravityRef.current,
      );
    }
    if (linear) {
      const f = filteredAccelRef.current;
      f[0] = lowpass(f[0], linear[0], dt, ACCEL_TAU_MS);
      f[1] = lowpass(f[1], linear[1], dt, ACCEL_TAU_MS);
      f[2] = lowpass(f[2], linear[2], dt, ACCEL_TAU_MS);
      const imuLongG = dot(f, frame.forward) / STANDARD_GRAVITY;
      const imuLatG = dot(f, frame.right) / STANDARD_GRAVITY;

      // Complementary blends (see BLEND_TAU_MS): sustained part from the
      // washout-proof reference (low-passed), transients from the IMU
      // (high-passed = raw − its own low-pass). Without a usable reference
      // (GPS dropout, no gyro) fall back to the raw IMU value.
      const speed = speedRef.current;

      let longG = imuLongG;
      const gpsAccel = gpsAccelRef.current;
      if (gpsAccel && now - gpsAccel.at <= GPS_ACCEL_MAX_AGE_MS) {
        lpRefLongRef.current = lowpass(lpRefLongRef.current, gpsAccel.g, dt, BLEND_TAU_MS);
        lpImuLongRef.current = lowpass(lpImuLongRef.current, imuLongG, dt, BLEND_TAU_MS);
        longG = lpRefLongRef.current + (imuLongG - lpImuLongRef.current);
      }

      let latG = imuLatG;
      if (sawGyroRef.current && speed != null && !Number.isNaN(speed)) {
        // Earth-frame cornering acceleration — what the friction circle wants.
        // The bike-frame IMU lateral reads ~0 in any coordinated corner, so the
        // sustained component must come from v·ω.
        const refLatG = (speed * filteredYawRef.current) / STANDARD_GRAVITY;
        lpRefLatRef.current = lowpass(lpRefLatRef.current, refLatG, dt, BLEND_TAU_MS);
        lpImuLatRef.current = lowpass(lpImuLatRef.current, imuLatG, dt, BLEND_TAU_MS);
        latG = lpRefLatRef.current + (imuLatG - lpImuLatRef.current);
      }

      const v = valuesRef.current;
      v.longG = longG;
      v.latG = latG;
      v.peakAccelG = Math.max(v.peakAccelG, longG);
      v.peakBrakeG = Math.max(v.peakBrakeG, -longG);
      v.peakLatG = Math.max(v.peakLatG, Math.abs(latG));
      trailRef.current.push({ at: Date.now(), longG, latG });
    }
  }, []);

  const onOrientation = useCallback((e: DeviceOrientationEvent) => {
    sawEventRef.current = true;
    const frame = frameRef.current;
    if (!frame || e.alpha == null || e.beta == null || e.gamma == null) return;
    valuesRef.current.leanImuDeg = leanFromOrientation(
      orientationMatrix(e.alpha, e.beta, e.gamma),
      frame,
    );
  }, []);

  /**
   * Shared front half of calibrate/restore: permission, listeners, first-event
   * check. Returns false with the failure status already set.
   */
  const ensureSensors = useCallback(async (): Promise<boolean> => {
    if (typeof DeviceMotionEvent === 'undefined') {
      setStatus('unsupported');
      return false;
    }
    setStatus('requesting');
    frameRef.current = null; // stop deriving telemetry while (re)calibrating

    const motion = DeviceMotionEvent as unknown as PermissionGated;
    if (typeof motion.requestPermission === 'function') {
      invertRef.current = true; // requestPermission ⇒ iOS WebKit ⇒ inverted accel signs
      try {
        const result = await motion.requestPermission();
        // One OS prompt covers both sensors, but orientation has its own gate.
        const orientation =
          typeof DeviceOrientationEvent !== 'undefined'
            ? (DeviceOrientationEvent as unknown as PermissionGated)
            : undefined;
        await orientation?.requestPermission?.().catch(() => undefined);
        if (result !== 'granted') {
          setStatus('denied');
          return false;
        }
      } catch {
        setStatus('denied');
        return false;
      }
    }

    if (!listenersRef.current) {
      listenersRef.current = true;
      window.addEventListener('devicemotion', onMotion);
      window.addEventListener('deviceorientation', onOrientation);
    }

    // Permission granted but no events ⇒ no sensors behind the API (desktop).
    if (!sawEventRef.current && !(await waitFor(() => sawEventRef.current, FIRST_EVENT_TIMEOUT_MS))) {
      setStatus('unsupported');
      return false;
    }
    return true;
  }, [onMotion, onOrientation]);

  /** Resets filters/trail and switches telemetry derivation to the given frame. */
  const activate = useCallback((frame: BikeFrame, gravity: Vec3) => {
    gravityRef.current = gravity;
    frameRef.current = frame;
    filteredAccelRef.current = [0, 0, 0];
    filteredYawRef.current = 0;
    lpRefLongRef.current = 0;
    lpImuLongRef.current = 0;
    lpRefLatRef.current = 0;
    lpImuLatRef.current = 0;
    gpsAccelRef.current = null;
    prevSpeedFixRef.current = null;
    lastMotionAtRef.current = 0;
    trailRef.current = [];
    valuesRef.current = { ...EMPTY_TELEMETRY };
    setStatus('active');
  }, []);

  const calibrate = useCallback(async () => {
    if (!(await ensureSensors())) return;

    setStatus('calibrating');
    calibrationSamplesRef.current = [];
    collectingRef.current = true;
    await delay(CALIBRATION_MS);
    collectingRef.current = false;

    const samples = calibrationSamplesRef.current;
    if (samples.length < 5) {
      setStatus('unsupported');
      return;
    }
    const { mean, maxDeviation } = calibrationStats(samples);
    if (maxDeviation > MAX_CALIBRATION_DEVIATION) {
      setStatus('calibration-failed');
      return;
    }
    const frame = deriveBikeFrame(mean);
    if (!frame) {
      setStatus('calibration-failed');
      return;
    }

    // Store gravity at the calibrated magnitude (not the nominal 9.81) so the
    // no-gyro fallback subtraction cancels the sensor's own bias too.
    const gravity = scale(frame.up, dot(mean, frame.up));
    storeCalibration({ ...frame, gravity, savedAt: Date.now() });
    setHasSavedCalibration(true);
    activate(frame, gravity);
  }, [ensureSensors, activate]);

  const restoreCalibration = useCallback(async () => {
    const stored = loadStoredCalibration();
    if (!stored) {
      // Storage vanished since the button rendered — fall back to a fresh run.
      setHasSavedCalibration(false);
      await calibrate();
      return;
    }
    if (!(await ensureSensors())) return;
    activate({ up: stored.up, forward: stored.forward, right: stored.right }, stored.gravity);
  }, [ensureSensors, activate, calibrate]);

  // Publish to React at a readable cadence; the canvas/trail consumers don't
  // need (and shouldn't pay for) 60 Hz state updates.
  useEffect(() => {
    if (status !== 'active') return;
    const id = setInterval(() => {
      const cutoff = Date.now() - TRAIL_MS;
      const trail = trailRef.current;
      while (trail.length && trail[0].at < cutoff) trail.shift();
      setTelemetry({ ...valuesRef.current, trail: [...trail] });
    }, PUBLISH_MS);
    return () => clearInterval(id);
  }, [status]);

  useEffect(
    () => () => {
      if (listenersRef.current) {
        window.removeEventListener('devicemotion', onMotion);
        window.removeEventListener('deviceorientation', onOrientation);
      }
    },
    [onMotion, onOrientation],
  );

  return { status, telemetry, calibrate, hasSavedCalibration, restoreCalibration };
}
